import React, { useState, useEffect, useCallback } from 'react';
import { listarImoveis, removerImovel, obterUrlImagem, atualizarImovel } from '../../services/api';
import styles from './ConsultarImoveis.module.css';

const ConsultarImoveis = () => {
  const [imoveis, setImoveis] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [limite, setLimite] = useState(20);
  const [idEmEdicao, setIdEmEdicao] = useState('');
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [erroEdicao, setErroEdicao] = useState('');
  const [camposEdicao, setCamposEdicao] = useState({
    doc_proprietario: '',
    nome: '',
    endereco: '',
    area_construida: '',
    area_total: '',
    descricao: '',
    telefone: '',
    tipo_imovel: '',
    tipo_operacao: '',
    valor: '',
    tags: '',
    imagensChave: '',
    imagemChave: '',
  });

  const buscarImoveis = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const dados = await listarImoveis(1, limite);
      setImoveis(dados.imoveis || []);
    } catch (err) {
      setErro(err.message || 'Erro ao carregar imóveis.');
    } finally {
      setCarregando(false);
    }
  }, [limite]);

  useEffect(() => {
    buscarImoveis();
  }, [buscarImoveis]);

  const handleRemover = async (id) => {
    if (!window.confirm('Confirma a remoção deste imóvel?')) return;
    try {
      await removerImovel(id);
      setImoveis((prev) => prev.filter((im) => im.id !== id));
    } catch (err) {
      alert('Erro ao remover: ' + err.message);
    }
  };

  const abrirEdicao = (imovel) => {
    setErroEdicao('');
    setIdEmEdicao(imovel.id);
    setCamposEdicao({
      doc_proprietario: imovel.doc_proprietario || '',
      nome: imovel.nome || '',
      endereco: imovel.endereco || '',
      area_construida: imovel.area_construida || '',
      area_total: imovel.area_total || '',
      descricao: imovel.descricao || '',
      telefone: imovel.telefone || '',
      tipo_imovel: imovel.tipo_imovel || '',
      tipo_operacao: imovel.tipo_operacao || '',
      valor: imovel.valor || '',
      tags: Array.isArray(imovel.tags) ? imovel.tags.join(', ') : '',
      imagensChave: Array.isArray(imovel.imagensChave) ? imovel.imagensChave.join(', ') : '',
      imagemChave: imovel.imagemChave || '',
    });
  };

  const fecharEdicao = () => {
    setIdEmEdicao('');
    setErroEdicao('');
  };

  const atualizarCampoEdicao = (e) => {
    const { name, value } = e.target;
    setCamposEdicao((prev) => ({ ...prev, [name]: value }));
  };

  const salvarEdicao = async (e) => {
    e.preventDefault();
    if (!idEmEdicao) return;

    setSalvandoEdicao(true);
    setErroEdicao('');

    const listaImagens = camposEdicao.imagensChave
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

    const payload = {
      doc_proprietario: camposEdicao.doc_proprietario.trim(),
      nome: camposEdicao.nome.trim(),
      endereco: camposEdicao.endereco.trim() || null,
      area_construida: camposEdicao.area_construida ? Number(camposEdicao.area_construida) : null,
      area_total: camposEdicao.area_total ? Number(camposEdicao.area_total) : null,
      descricao: camposEdicao.descricao.trim() || null,
      telefone: camposEdicao.telefone.trim() || null,
      tipo_imovel: camposEdicao.tipo_imovel.trim(),
      tipo_operacao: camposEdicao.tipo_operacao.trim(),
      valor: camposEdicao.valor ? Number(camposEdicao.valor) : undefined,
      tags: camposEdicao.tags
        ? camposEdicao.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      imagensChave: listaImagens,
      imagemChave: camposEdicao.imagemChave.trim() || null,
    };

    try {
      const dados = await atualizarImovel(idEmEdicao, payload);
      setImoveis((prev) => prev.map((im) => (im.id === idEmEdicao ? dados.imovel : im)));
      fecharEdicao();
    } catch (err) {
      setErroEdicao(err.message || 'Erro ao salvar edição.');
    } finally {
      setSalvandoEdicao(false);
    }
  };

  return (
    <div className={`${styles.container} ui-surface`}>
      <div className={styles.cabecalho}>
        <h2>Imóveis Cadastrados</h2>
        <div className={styles.controles}>
          <label>
            Exibir:&nbsp;
            <select className="ui-input" value={limite} onChange={(e) => setLimite(Number(e.target.value))}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button className="ui-button-primary" onClick={buscarImoveis} disabled={carregando}>
            {carregando ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
      </div>

      {erro && <p className={styles.erro}>{erro}</p>}

      {!carregando && imoveis.length === 0 && !erro && (
        <p className={styles.vazio}>Nenhum imóvel encontrado.</p>
      )}

      <div className={styles.lista}>
        {imoveis.map((imovel) => (
          <div key={imovel.id} className={`${styles.card} ui-surface`}>
            {imovel.imagemChave && (
              <img
                src={obterUrlImagem(imovel.imagemChave)}
                alt="Capa do imóvel"
                className={styles.capa}
              />
            )}
            <div className={styles.info}>
              <p className={styles.endereco}>{imovel.endereco || '—'}</p>
              <p className={styles.proprietario}>
                <strong>Proprietário:</strong> {imovel.nome}
              </p>
              <p>
                <strong>Tipo:</strong> {imovel.tipo_imovel} &nbsp;|&nbsp;
                <strong>Operação:</strong> {imovel.tipo_operacao}
              </p>
              {imovel.valor && (
                <p>
                  <strong>Valor:</strong>{' '}
                  {Number(imovel.valor).toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </p>
              )}
              {imovel.descricao && <p className={styles.descricao}>{imovel.descricao}</p>}
              {imovel.tags && imovel.tags.length > 0 && (
                <div className={styles.tags}>
                  {imovel.tags.map((tag, i) => (
                    <span key={i} className={styles.tag}>{tag}</span>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.acoes}>
              <button
                className={`${styles.btnEditar} ui-button-primary`}
                onClick={() => abrirEdicao(imovel)}
              >
                Editar
              </button>
              <button
                className={`${styles.btnRemover} ui-button-primary`}
                onClick={() => handleRemover(imovel.id)}
              >
                Remover
              </button>
            </div>

            {idEmEdicao === imovel.id && (
              <form className={styles.formEdicao} onSubmit={salvarEdicao}>
                <h3>Editar Imóvel</h3>

                <label>Doc Proprietário *</label>
                <input className="ui-input" name="doc_proprietario" value={camposEdicao.doc_proprietario} onChange={atualizarCampoEdicao} required />

                <label>Nome *</label>
                <input className="ui-input" name="nome" value={camposEdicao.nome} onChange={atualizarCampoEdicao} required />

                <label>Endereço</label>
                <input className="ui-input" name="endereco" value={camposEdicao.endereco} onChange={atualizarCampoEdicao} />

                <label>Telefone</label>
                <input className="ui-input" name="telefone" value={camposEdicao.telefone} onChange={atualizarCampoEdicao} />

                <div className={styles.gridEdicao}>
                  <div>
                    <label>Tipo Imóvel *</label>
                    <input className="ui-input" name="tipo_imovel" value={camposEdicao.tipo_imovel} onChange={atualizarCampoEdicao} required />
                  </div>
                  <div>
                    <label>Tipo Operação *</label>
                    <input className="ui-input" name="tipo_operacao" value={camposEdicao.tipo_operacao} onChange={atualizarCampoEdicao} required />
                  </div>
                </div>

                <div className={styles.gridEdicao}>
                  <div>
                    <label>Valor</label>
                    <input className="ui-input" type="number" step="0.01" name="valor" value={camposEdicao.valor} onChange={atualizarCampoEdicao} />
                  </div>
                  <div>
                    <label>Área Construída</label>
                    <input className="ui-input" type="number" step="0.01" name="area_construida" value={camposEdicao.area_construida} onChange={atualizarCampoEdicao} />
                  </div>
                  <div>
                    <label>Área Total</label>
                    <input className="ui-input" type="number" step="0.01" name="area_total" value={camposEdicao.area_total} onChange={atualizarCampoEdicao} />
                  </div>
                </div>

                <label>Descrição</label>
                <textarea className="ui-input" name="descricao" rows="3" value={camposEdicao.descricao} onChange={atualizarCampoEdicao} />

                <label>Tags (separadas por vírgula)</label>
                <input className="ui-input" name="tags" value={camposEdicao.tags} onChange={atualizarCampoEdicao} />

                <label>Imagens Chave (separadas por vírgula)</label>
                <input className="ui-input" name="imagensChave" value={camposEdicao.imagensChave} onChange={atualizarCampoEdicao} />

                <label>Imagem de Capa</label>
                <input className="ui-input" name="imagemChave" value={camposEdicao.imagemChave} onChange={atualizarCampoEdicao} />

                {erroEdicao && <p className={styles.erro}>{erroEdicao}</p>}

                <div className={styles.acoesEdicao}>
                  <button type="button" className={`${styles.btnCancelar} ui-button-primary`} onClick={fecharEdicao}>Cancelar</button>
                  <button type="submit" className={`${styles.btnSalvar} ui-button-primary`} disabled={salvandoEdicao}>
                    {salvandoEdicao ? 'Salvando...' : 'Salvar Alterações'}
                  </button>
                </div>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConsultarImoveis;
